import React from 'react';
import ProfileEditButton from './ProfileEditButton';
import styles from '../Profile.module.css';

interface ProfileInformationSectionProps {
  name: string;
  email?: string;
  memberSince: string;
  isEditable?: boolean;
  onEdit?: () => void;
}

const iconUrl = (fileName: string) => `${process.env.PUBLIC_URL}/icons/${fileName}`;
const informationIconStyle = (fileName: string) => ({
  '--profile-information-icon-url': `url("${iconUrl(fileName)}")`,
}) as React.CSSProperties;

const ProfileInformationSection: React.FC<ProfileInformationSectionProps> = ({
  name,
  email,
  memberSince,
  isEditable = false,
  onEdit,
}) => {
  const rows = [
    {label: 'Name', value: name, icon: 'user-fill.svg'},
    ...(email ? [{label: 'Email', value: email, icon: 'message-fill.svg'}] : []),
    {label: 'Member Since', value: memberSince, icon: 'calendar-filled.svg'},
  ];

  return (
    <section
      className={`${styles.profileSection} ${styles.profileInformationSection}`}
      aria-labelledby="profile-information-title"
    >
      <div className={styles.profileInformationHeader}>
        <div className={styles.profileInformationHeading}>
          <div className={styles.profileInformationTitleRow}>
            <span className={styles.profileInformationHeadingIconTile} aria-hidden="true">
              <span
                className={styles.profileInformationHeadingIcon}
                style={informationIconStyle('user-fill.svg')}
              />
            </span>
            <h3
              id="profile-information-title"
              className={`${styles.sectionTitle} ${styles.profileInformationTitle}`}
            >
              Profile Information
            </h3>
          </div>
          <p className={styles.profileInformationSubtitle}>Your personal details and account information.</p>
        </div>

        {isEditable && onEdit ? (
          <ProfileEditButton
            className={styles.profileInformationEditButton}
            iconClassName={styles.profileInformationEditIcon}
            iconStyle={informationIconStyle('edit-filled.svg')}
            onClick={onEdit}
          />
        ) : null}
      </div>

      <div className={styles.profileInformationDivider} />

      <div className={styles.profileDetails}>
        {rows.map((row) => (
          <div key={row.label} className={styles.detailRow}>
            <span className={styles.detailIconTile} aria-hidden="true">
              <span
                className={styles.detailIcon}
                style={informationIconStyle(row.icon)}
              />
            </span>
            <div className={styles.detailContent}>
              <span className={styles.detailLabel}>{row.label}</span>
              <span className={styles.detailValue} title={row.value}>{row.value}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default React.memo(ProfileInformationSection);
